import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import useAppDispatch from "@/core/helpers/useAppDispatch";
import useAppSelector from "@/core/helpers/useAppSelector";
import Loader from "@/shared/ui/Loader";
import DeviceContentWrapper from "@/shared/widgets/DeviceContentWrapper";
import DeviceTabButton from "@/shared/widgets/DeviceTabButton";
import DeviceTabsWrapper from "@/shared/widgets/DeviceTabsWrapper";
import { DeviceEnum } from "@/domains/business/constants/deviceEnum";
import { appCurrentTabSelector, pcInfoSelector, setCurrentTab } from "@/domains/business/slice";
import { getBusinessTabContent } from "@/domains/business/helpers/getBusinessTabContent";
import useGetPcInfo from "@/domains/business/hooks/useGetPcInfo";
import useHidePc from "@/domains/business/hooks/useHidePc";
import PcContainer from "@/domains/pc/containers/PcContainer";

type Props = {
  businessId: number;
};

export default function BusinessPcContainer({ businessId }: Props) {
  const { t } = useTranslation("business");
  const dispatch = useAppDispatch();

  const info = useAppSelector(pcInfoSelector);
  const appCurrentTab = useAppSelector(appCurrentTabSelector);

  const { hidePc } = useHidePc();
  useGetPcInfo(businessId);

  const tabContent = useMemo(() => getBusinessTabContent(DeviceEnum.PC, info?.role), [info?.role]);

  const Component = tabContent ? tabContent.tabsMap[appCurrentTab] : null;

  let content = <Loader />;

  if (appCurrentTab && tabContent) {
    content = (
      <div className="size-full bg-gray-950" data-mz-accent="lightBlue">
        <DeviceContentWrapper>
          <DeviceTabsWrapper
            logoSrc="/svg/business/my-business.svg"
            title={t("business.header.title")}
            description={t("business.header.description")}
            isBackground
            className="bg-gradient-to-r from-[#007FDB] to-[#0164AC]"
          >
            {tabContent.tabsList.map((item) => (
              <DeviceTabButton
                key={item}
                tab={item}
                active={appCurrentTab === item}
                text={t(`business.tabs.${item}`)}
                iconCategory="business/tabs"
                iconName={item}
                onClick={() => dispatch(setCurrentTab(item))}
              />
            ))}
          </DeviceTabsWrapper>
          <Component onClose={hidePc} info={info} device={DeviceEnum.PC} />
        </DeviceContentWrapper>
      </div>
    );
  }

  return <PcContainer>{content}</PcContainer>;
}
